import { Component, OnInit, ViewChild } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { IwsFile, Query, WsDynamicPage, WsFileType, WsTextContent } from 'core-lib';
import { MenuItem, TreeNode } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ContextMenu } from 'primeng/contextmenu';

@Component({
    selector: 'lib-workspace-picker',
    templateUrl: './workspace-picker.component.html',
    styleUrls: ['./workspace-picker.component.scss'],
    standalone: false
})
export class WorkspacePickerComponent implements OnInit {

  workspaceFiles: TreeNode[] = [];
  selectedFile: TreeNode | TreeNode[];
  requestedFileTypes: WsFileType[] = [];
  @ViewChild("sm") sm: ContextMenu;

  sortMenuOptions: MenuItem[] = [
    { label: $localize`:@@workspace-explorer.tree.sort.last-change-asc:Last modified: ascending`, icon: 'pi pi-sort-amount-up', command: (event) => { this.sortForNode(this.workspaceFiles[0], 'last-change-asc') } },
    { label: $localize`:@@workspace-explorer.tree.sort.title-asc:Filename: ascending`, icon: 'pi pi-sort-amount-up', command: (event) => { this.sortForNode(this.workspaceFiles[0], 'title-asc') } },
    { label: $localize`:@@workspace-explorer.tree.sort.title-descending:Filename: descending`, icon: 'pi pi-sort-amount-down-alt', command: (event) => { this.sortForNode(this.workspaceFiles[0], 'title-desc') } },
    { label: $localize`:@@workspace-explorer.tree.sort.last-change-descending:Last modified: descending`, icon: 'pi pi-sort-amount-down-alt', command: (event) => { this.sortForNode(this.workspaceFiles[0], 'last-change-desc') } }
  ];

  activeFilterSearchWord: string;

  BASIC_FIELDS = `id
  type 
  key
  name
  lastModified
  lastModifiedBy
  description
  parentId
  readonly
  size`;

  WS_CONTENT_PROPERTIES = `
  ... on WSPage {
    ${this.BASIC_FIELDS}
    url
  }
  ... on WSCss {
    ${this.BASIC_FIELDS}
    url
  }
  ... on WSJavaScript {
    ${this.BASIC_FIELDS}
    url
  }
  ... on WSDynamicPage {
    ${this.BASIC_FIELDS}
    url
    templateUrl
    targetComponentId
    targetComponentType
    sharedResourceId
    cssCode
  }
  ... on WSJson {
    ${this.BASIC_FIELDS}
    url
  }`;

  QUERY_WORKSPACE = gql`
  query listWorkspaceFiles($parent: WSFileId!){
    listWorkspaceFiles(parent: $parent){
      ${this.BASIC_FIELDS}
      ... on WSFolder {
        children
      }
      ${this.WS_CONTENT_PROPERTIES}
      ... on WSLink {
        ${this.BASIC_FIELDS}
        url
      }
    }
  }`;

  QUERY_WORKSPACE_ROOT = gql`
  query getWorkspaceRoot{
    workspaceRoot{
      key
      id 
      type
      name
    }
  }`;

  constructor(private apollo: Apollo, public dialogRef: DynamicDialogRef, private dialogConfig: DynamicDialogConfig) {
    this.requestedFileTypes = dialogConfig.data?.requestedFileTypes;
    if (dialogConfig.data?.selectedFile) {
      this.selectedFile = this.asTreeNode(dialogConfig.data?.selectedFile, true);
      if (this.selectedFile) {
        setTimeout(() => {
          let file = this.selectedFile as TreeNode
          const node = document.getElementById(file.data.id);
          if (node) {
            node.scrollIntoView({
              behavior: 'auto',
              block: 'center',
              inline: 'center'
            });
          }
        }, 500)
      }
    }
    
  }

  refresh(): void {
    this.apollo.query<Query>({
      query: this.QUERY_WORKSPACE_ROOT,
      variables: {}
    }).subscribe((result) => {
      if (!result.data.workspaceRoot) {
        return;
      }
      const workspaceRootTreeNode = this.asTreeNode(result.data.workspaceRoot, true);
      const treeMap = new Map<string, TreeNode[]>();
      this.apollo.query<Query>({
        query: this.QUERY_WORKSPACE,
        variables: { parent: result.data.workspaceRoot.id }
      }).subscribe((result) => {
        let workspaceFiles = result.data.listWorkspaceFiles;

        console.log(this.activeFilterSearchWord);
        if (this.activeFilterSearchWord && this.activeFilterSearchWord !== '') {
          workspaceFiles = workspaceFiles.filter((workspaceFile) => {
            if (workspaceFile.type === WsFileType.Folder || workspaceFile.type === WsFileType.Root) {
              return true;
            }
            if (workspaceFile.name.toLocaleLowerCase().indexOf(this.activeFilterSearchWord.toLocaleLowerCase()) > -1) {
              return true;
            }
            return false;
          });
        }

        workspaceFiles.forEach((file) => {
          let childList = treeMap.get(file.parentId);
          if (childList == null) {
            childList = [];
          }
          childList.push(this.asTreeNode(file, true));
          treeMap.set(file.parentId, childList);
        });
        this.buildTreeRecursive(treeMap, workspaceRootTreeNode);
        this.workspaceFiles = [workspaceRootTreeNode];
      });
    });
  }
  /**
   * 
   * @param file 
   * @returns 
   */
  asTreeNode(file: IwsFile, expanded: boolean): TreeNode {
    let node = { leaf: true, data: { ...file }, children: [], label: file.name, key: file.id, expanded: expanded } as TreeNode;
    if (file.type === 'FOLDER') {
      node.expandedIcon = "pi pi-folder-open";
      node.collapsedIcon = "pi pi-folder";
    } else if (file.type === WsFileType.Page) {
      node.icon = 'browser-icon html';
    } else if (file.type === WsFileType.DynamicPage) {
      node.icon = 'browser-icon dynamic';
      const dynamicPage = file as WsDynamicPage;
      if (dynamicPage.sharedResourceId) {
        node.icon = 'browser-icon global-dynamic';
      }
    } else if (file.type === WsFileType.Css) {
      node.icon = 'browser-icon css';
    } else if (file.type === WsFileType.Javascript) {
      node.icon = 'browser-icon js';
    } else if (file.type === WsFileType.Json) {
      node.icon = 'browser-icon json';
    } else if (file.type === WsFileType.Link) {
      node.icon = 'browser-icon link';
    } else if (file.type === WsFileType.MailTemplate) {
      node.icon = 'browser-icon mailtemplate';
    }
    else if (file.type === WsFileType.Root) {
      node.expanded = true;
      node.styleClass = 'root-tree-node';
      node.icon = 'fal fa-none'
      node.type = 'ROOT'
    }
    if (file.type === WsFileType.Page || file.type === WsFileType.Css || file.type === WsFileType.Javascript || file.type === WsFileType.Json) {
      const page = file as WsTextContent;
      node.data.url = page.url;
    }
    return node;
  }

  ngOnInit(): void {
    this.refresh();
  }

  buildTreeRecursive(treeMap: Map<string, TreeNode[]>, parent: TreeNode) {
    parent.children = treeMap.get(parent.key);
    if (parent.children) {
      parent.children.forEach((node) => this.buildTreeRecursive(treeMap, node));
    }
  }

  /**
   * 
   * @returns 
   */
  isValidSelection(): boolean {
    if (this.selectedFile) {
      let file = this.selectedFile as TreeNode
      if (this.requestedFileTypes.indexOf(file.data.type) > -1) {
        return true;
      }
    }
    return false;
  }

  openSortMenuForItem(event, node: TreeNode): void {
    event.preventDefault();
    event.stopPropagation();
    if (node.data.readonly) {
      return;
    }
    this.sm.toggle(event);
  }

  sortForNode(node: TreeNode, sortType: string): void {
    if (node.children) {
      node.children.forEach((child) => {
        this.sortForNode(child, sortType);
      });
      if (sortType === 'title-asc') {
        node.children.sort((a, b) => {
          return a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase());
        });
      }
      if (sortType === 'title-desc') {
        node.children.sort((a, b) => {
          return b.label.toLocaleLowerCase().localeCompare(a.label.toLocaleLowerCase());
        });
      }
      if (sortType === 'last-change-asc') {
        node.children.sort((a, b) => {
          return new Date(a.data.lastModified).getTime() - new Date(b.data.lastModified).getTime();
        });
      }
      if (sortType === 'last-change-desc') {
        node.children.sort((a, b) => {
          return new Date(b.data.lastModified).getTime() - new Date(a.data.lastModified).getTime();
        });
      }
    }
  }

  chooseDoubleClick(): void {
    if (this.isValidSelection()) {
      let file = this.selectedFile as TreeNode
      this.dialogRef.close(file.data);
    }
  }

  closeDialog(file: TreeNode | TreeNode[]) : void{
    file = file as TreeNode;
    this.dialogRef.close(file.data)
  }
}
